import devLog from './devLog';
/**
 * DeploymentSyncManager - V32.5.0 CACHE NUKE EDITION
 * 
 * Automatically detects new backend deployments and clears stale browser data.
 * V32.5.0 Update: Added forceful cache busting for UI updates.
 */

const DEPLOYMENT_ID_KEY = 'ep_deployment_id';
const LAST_SYNC_KEY = 'ep_last_deployment_sync';
const SYNC_COOLDOWN_MS = 5_000; // Reduced cooldown for rapid testing

/**
 * Keys that must NEVER be cleared (auth/session continuity)
 */
const PROTECTED_KEYS = new Set([
  'ep_session_token',
  'ep_session_backup',
  'ep_user_email',
  'ep_user',
  'ep_session_id',
  'ep_deployment_id',
  'ep_last_deployment_sync',
  'ep_cache_version',
]);

/**
 * Patterns of stale data to purge
 */
const STALE_PATTERNS = [
  'credit', 'deduct', 'pending_credit', 'deduction_',
  'ep_credit_', 'ep_deduct_', 'credit_cache_',
  'ep_result_', 'ep_generation_lock', 'ep_session_lock',
  'stale_', '_temp_', '_cache_', 'credit_pending',
  'deduction_in_progress', 'lock_',
  // V32.5.0: Nuke all results to force UI refresh
  'result_', 'ep_latest_result' 
];

const isStaleKey = (key) => {
  if (PROTECTED_KEYS.has(key)) return false;
  const lower = key.toLowerCase();
  return STALE_PATTERNS.some(p => lower.includes(p));
};

const purgeStaleStorage = () => {
  let cleared = 0;
  
  // localStorage
  Object.keys(localStorage).forEach(key => {
    if (isStaleKey(key)) {
      localStorage.removeItem(key);
      cleared++;
    }
  });
  
  // sessionStorage
  Object.keys(sessionStorage).forEach(key => {
    if (isStaleKey(key)) {
      sessionStorage.removeItem(key);
      cleared++;
    }
  });
  
  return cleared;
};

const purgeBlockingCookies = () => {
  const cookieBlockList = ['credit_', 'pending_', 'lock_', '_csrf', 'XSRF'];
  const domains = ['', window.location.hostname];
  
  try {
    document.cookie.split(';').forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      const lower = name.toLowerCase();
      if (cookieBlockList.some(p => lower.includes(p))) {
        domains.forEach(domain => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          if (domain) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
          }
        });
      }
    });
  } catch (e) { devLog.warn('[DeploySync] Cookie purge failed:', e.message); }
};

const purgeServiceWorkerCaches = async () => {
  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(name => caches.delete(name)));
    }
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }
  } catch (e) { devLog.warn('[DeploySync] SW cache purge failed:', e.message); }
};

export const syncWithDeployment = async () => {
  const lastSync = parseInt(localStorage.getItem(LAST_SYNC_KEY) || '0', 10);
  if (Date.now() - lastSync < SYNC_COOLDOWN_MS) return false;
  
  localStorage.setItem(LAST_SYNC_KEY, Date.now().toString());
  
  try {
    // V32.5.0: Force backend to return current deployment ID
    // We use a timestamp to bypass Nginx caching of this request
    const backendUrl = (typeof process !== 'undefined' ? process.env.REACT_APP_BACKEND_URL : '') || '';
    const res = await fetch(`${backendUrl}/api/version?_cb=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
    });
    
    if (!res.ok) return false;
    
    const { deployment_id, version } = await res.json();
    if (!deployment_id) return false;
    
    const storedId = localStorage.getItem(DEPLOYMENT_ID_KEY);
    
    // Always update stored ID
    if (storedId !== deployment_id) {
        devLog.log(`[DeploySync] New deployment detected: ${storedId} → ${deployment_id} (v${version})`);
        
        // Execute Nuclear Purge
        const clearedCount = purgeStaleStorage();
        purgeBlockingCookies();
        await purgeServiceWorkerCaches();
        
        localStorage.setItem(DEPLOYMENT_ID_KEY, deployment_id);
        
        devLog.log(`[DeploySync] Purge complete: ${clearedCount} entries cleared.`);
        return true;
    }
    
    return false;
  } catch (err) {
    devLog.warn('[DeploySync] Sync failed (non-fatal):', err.message);
    return false;
  }
};

export default { syncWithDeployment };
